/**
 * ExtraPipSkillEffect — Specter wraith-path Evasion passive: +1 max dash charge.
 *
 * On equip: sets `bonusMaxCharges = 1` on DashComponent, raising the cap from
 * 1 to 2. On unequip: resets to 0. The component setter handles charge clamping
 * and emits `dash-changed` automatically.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-drift-path-proposal.md § Extra Pip
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ExtraPipDeps {
  readonly dash: {
    /** Extra charges granted by passives (e.g. Extra Pip). */
    bonusMaxCharges: number;
  };
}

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ExtraPipSkillEffect implements SkillEffect {
  readonly skillId = 'extra_pip';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;
  readonly staminaCost: number = 0;

  private readonly deps: ExtraPipDeps;

  /** Whether the bonus has been applied (guards against double-application). */
  private _applied = false;

  constructor(deps: ExtraPipDeps) {
    this.deps = deps;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean { return false; }
  activate(): boolean { return false; }
  deactivate(): void {}
  isActive(): boolean { return false; }

  update(_delta: number): void {
    // Grant +1 bonus dash charge on first update tick (equip).
    if (!this._applied) {
      this.deps.dash.bonusMaxCharges = 1;
      this._applied = true;
    }
  }

  destroy(): void {
    // Reset bonus on unequip
    if (this._applied) {
      this.deps.dash.bonusMaxCharges = 0;
      this._applied = false;
    }
  }
}
